import React from "react";
import Layout from "../Landing/Layout";
import mandateLed from "../Landing/assets/mandate-led.png";
import leadershipImage from "../Landing/assets/leadership.webp";

const WhyPartner = () => {
  return (
    <Layout>
      <section className="page-hero">
        <div className="section-container animate-fade-in-up">
          <div className="grid-12">
            <div className="lg:col-span-8 lg:col-start-3 text-center">
              <h1 className="heading-display mb-8">A Platform Designed for Serious Professionals</h1>

              <div className="space-y-6 text-measure mx-auto">
                <p className="body-text text-muted-foreground">
                  Douglas & McCartney is designed for brokers and advisors who want to build credible, long-term advisory businesses.
                </p>
                <p className="body-text text-muted-foreground">
                  Partnership is not about employment or short-term opportunity. It is about alignment with a structured platform that supports sustainable professional growth.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-band section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-4">Mandate Led, Not Lead Dependent</h2>
            <p className="body-text text-muted-foreground text-measure mx-auto">
              Douglas & McCartney operates on a mandate-holding model rather than a lead-chasing brokerage approach.
            </p>
          </div>

          <div className="grid-12 items-center">
            <div className="image-frame lg:col-span-6">
              <img
                src={mandateLed}
                alt="Mandate-Led Business Model"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
            <div className="space-y-6 lg:col-span-6">
              <div>
                <h3 className="heading-subsection heading-mid mb-4">The platform focuses on:</h3>
                <ul className="space-y-3">
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Securing and managing developer mandates
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Structured project distribution across the partner network
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Centralized compliance and process control
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Consistent execution standards
                  </li>
                </ul>
              </div>

              <p className="body-text text-muted-foreground">
                This model allows partners to participate in larger, structured opportunities rather than relying solely on individual lead generation.
              </p>

              <div className="pt-4 space-y-2">
                <p className="heading-subsection text-accent">Mandates create predictability.</p>
                <p className="heading-subsection text-accent">Systems create scalability.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-surface section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-4">The Structural Challenge</h2>
            <p className="body-text text-muted-foreground text-measure mx-auto">
              Independent advisors often face challenges that are systemic rather than personal:
            </p>
          </div>

          <div className="grid-12 gap-8">
            <div className="lg:col-span-8 lg:col-start-3">
              <ul className="space-y-3 text-center">
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Growth depends on individual effort
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Business continuity is fragile
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Brand recognition remains limited
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Advisory practices lack consistency
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Strategic decision-making is informal
                </li>
              </ul>
              <p className="body-text text-muted-foreground mt-6 text-center">
                Over time, these constraints limit long-term potential.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-band section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-4">What the Platform Enables</h2>
            <p className="body-text text-muted-foreground text-measure mx-auto">
              Douglas & McCartney provides:
            </p>
          </div>

          <div className="grid-12 gap-8">
            <div className="lg:col-span-8 lg:col-start-3">
              <ul className="space-y-3 text-center">
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Defined advisory frameworks
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Operational clarity and governance
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Brand alignment and positioning
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  Strategic guidance and mentorship
                </li>
                <li className="body-text font-semibold flex items-center justify-center gap-3 text-center">
                  <span className="text-accent font-bold">&rsaquo;</span>
                  A long-term professional pathway
                </li>
              </ul>
              <p className="body-text text-muted-foreground mt-6 italic text-center">
                The platform supports independence - it does not replace it.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-surface section-divider">
        <div className="section-container">
          <div className="section-header text-center">
            <h2 className="heading-section mb-4">Mentorship & Strategic Guidance</h2>
            <p className="body-text text-muted-foreground text-measure mx-auto">
              Douglas & McCartney provides access to structured mentorship and strategic guidance from experienced industry leadership.
            </p>
          </div>

          <div className="grid-12 items-center">
            <div className="space-y-6 lg:col-span-6">
              <div>
                <h3 className="heading-subsection mb-4">Mentorship focuses on:</h3>
                <ul className="space-y-3">
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Advisory decision-making
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Business structuring
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Market positioning
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Long-term professional planning
                  </li>
                </ul>
              </div>

              <p className="body-text text-muted-foreground">
                As part of this leadership ecosystem, Dr. BM Pounacha provides direct mentorship and strategic guidance to partners. His experience informs advisory frameworks, governance standards, and the long-term direction of the platform.
              </p>

              <p className="body-text text-muted-foreground italic font-semibold">
                Mentorship is practical, measured, and outcome-focused.
              </p>
            </div>

            <div className="image-frame lg:col-span-6">
              <img
                src={leadershipImage}
                alt="Leadership and Mentorship"
                className="w-full h-auto"
                loading="lazy"
                decoding="async"
                sizes="(max-width: 1024px) 100vw, 50vw"
              />
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell section-band section-divider">
        <div className="section-container">
          <div className="grid-12">
            <div className="lg:col-span-8 lg:col-start-3">
              <div className="section-header">
                <h2 className="heading-section mb-4">Masterclasses & Professional Development</h2>

                <p className="body-text text-muted-foreground text-measure">
                  Douglas & McCartney places strong emphasis on continuous learning.
                </p>
              </div>

              <div className="mb-6">
                <h3 className="heading-subsection heading-mid mb-4">Partners gain access to:</h3>
                <ul className="space-y-3">
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Structured masterclasses led by experienced professionals
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Practical training across advisory, compliance, and execution
                  </li>
                  <li className="body-text font-semibold flex items-start gap-3">
                    <span className="text-accent font-bold">&rsaquo;</span>
                    Knowledge sessions focused on long-term advisory capability
                  </li>
                </ul>
              </div>

              <p className="body-text text-muted-foreground text-measure">
                The objective is to help professionals move beyond transactional broking toward disciplined advisory practice.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default WhyPartner;
